import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

export type CanaTiposErroTypes = 'DESAUTORIZADO';
export type CanalStatusTypes = 'CONECTANDO' | 'DESATIVADO';
export type CanalModoEnvio = 'API_OFICIAL' | 'API_NAO_OFICIAL';

export enum TipoConexao {
  WHATSAPP_QRCODE = 'WHATSAPP_QRCODE',
  WHATSAPP_CODE = 'WHATSAPP_CODE',
}

export declare interface CanalInterface {
  id: string;
  contato: string;
  nome: string;
  codigo: string;
  tipoCanal: TipoCanalTypes;
  isAtivo: boolean;
  canalId: string;
  online: boolean;
  status: CanalStatusTypes;
  statusEm: string;
  canalNome: string;
  mensagem: string;
  tipoErro: CanaTiposErroTypes;
  removidoDoGrupoEm: string;
  desautorizado: boolean;
  numeroContato: string;
  isCanalPreferencial?: boolean;
  isCanalOnline: boolean;
  isModoHibrido: boolean;
  modoEnvio: CanalModoEnvio;
}

export declare interface CanalInfoInterface {
  canalCodigo: string;
  canalId: string;
  canalRemoteId: string;
  canalTipo: TipoCanalEnum;
  canalBSP: string | null;
}

export declare interface CanalInfoInterfaceV2 {
  id: string;
  name: string;
  number: string;
  code: string;
  type: TipoCanalTypes;
  isHybridMode: boolean;
  typeAccess: 'PUBLICO' | 'PRIVADO';
  authorizedTeamIds?: string[];
}

export declare interface SelectChannelInterface {
  id: string;
  name: string;
  code: string;
  type: TipoCanalTypes;
  isHybridMode: boolean;
  isDefault: boolean;
}

export declare interface TipoCanalInterface {
  key: string;
  value: string;
}

export type TipoCanalTypes =
  | 'WHATSAPP'
  | 'TELEGRAM'
  | 'MESSENGER'
  | 'INSTAGRAM'
  | 'WHATSAPP_OFICIAL';

export enum TipoCanalEnum {
  WHATSAPP = 'WHATSAPP',
  TELEGRAM = 'TELEGRAM',
  MESSENGER = 'MESSENGER',
  INSTAGRAM = 'INSTAGRAM',
  WHATSAPP_OFICIAL = 'WHATSAPP_OFICIAL',
}

type CanaisState = {
  canaisOnline: CanalInterface[];
  loadingCanaisOnline: boolean;

  canaisSelect: SelectChannelInterface[];
  loadingCanaisSelect: boolean;

  tiposCanais: TipoCanalInterface[];
  loadingTiposCanais: boolean;
};

export const fetchCanaisOnlinePorTipo = createAsyncThunk(
  'canais/fetchCanaisOnlinePorTipo',
  async (params: { tipoCanal: TipoCanalTypes; contatoId: string }) => {
    const response = await chatAPI.get(
      `canais/${params.tipoCanal}/online?paraContatoId=${params.contatoId}`,
    );
    return response.data.data as CanalInterface[];
  },
);

export const fetchCanaisSelect = createAsyncThunk(
  'v2/canais/fetchCanaisSelect',
  async (params?: {
    isActive?: boolean;
    types?: TipoCanalTypes[];
    contactId?: string;
    equipeId?: string;
  }) => {
    const response = await chatAPI.get('v2/channels/select', {
      params,
    });
    return response.data as SelectChannelInterface[];
  },
);

export const fetchTiposCanais = createAsyncThunk(
  'canais/fetchTiposCanais',
  async () => {
    const response = await chatAPI.get('canais/tipos');
    return response.data.data as TipoCanalInterface[];
  },
);

const initialState = {
  canaisOnline: [],
  loadingCanaisOnline: false,

  canaisSelect: [],
  loadingCanaisSelect: false,

  tiposCanais: [],
  loadingTiposCanais: false,
} as CanaisState;

export const canaisSlice = createSlice({
  name: 'canais',
  initialState,
  reducers: {
    resetCanaisSelect: state => {
      state.canaisSelect = [];
    },
  },
  extraReducers(builder) {
    // Fetch canais online por tipo
    builder.addCase(fetchCanaisOnlinePorTipo.pending, state => {
      state.loadingCanaisOnline = true;
    });
    builder.addCase(fetchCanaisOnlinePorTipo.fulfilled, (state, action) => {
      state.canaisOnline = action.payload;
      state.loadingCanaisOnline = false;
    });
    // Fetch select de canais
    builder.addCase(fetchCanaisSelect.pending, state => {
      state.loadingCanaisSelect = true;
    });
    builder.addCase(fetchCanaisSelect.fulfilled, (state, action) => {
      state.canaisSelect = action.payload;
      state.loadingCanaisSelect = false;
    });
    builder.addCase(fetchCanaisSelect.rejected, state => {
      state.loadingCanaisSelect = false;
    });
    // Fetch tipos de canais
    builder.addCase(fetchTiposCanais.pending, state => {
      state.loadingTiposCanais = true;
    });
    builder.addCase(fetchTiposCanais.fulfilled, (state, action) => {
      state.tiposCanais = action.payload;
      state.loadingTiposCanais = false;
    });
    builder.addCase(fetchTiposCanais.rejected, state => {
      state.loadingTiposCanais = false;
    });
  },
});

export const { resetCanaisSelect } = canaisSlice.actions;
export const canaisSelector = (state: RootState) => state.canais;

export default canaisSlice.reducer;
